import { useLingui } from '@lingui/react/macro'
import { Box } from 'designSystem/components/Box'
import { Button } from 'designSystem/components/Button'
import { ScrollFade } from 'designSystem/components/ScrollFade'
import { Text } from 'designSystem/components/Text'
import { ActiveEditScopeBanner } from 'features/knowledgeDocumentViewer/components/metadata/ActiveEditScopeBanner'
import type { MetadataEditSurface } from 'features/knowledgeDocumentViewer/components/metadata/ChooseEditScopePanel'
import { EditableMetadataFieldList } from 'features/knowledgeDocumentViewer/components/metadata/EditableMetadataFieldList'
import { metadataFillLayout } from 'features/knowledgeDocumentViewer/components/metadata/metadataFillLayout'
import {
  BannerIcon,
  ConfirmBanner,
  WarningBanner,
  WarningIcon,
} from 'features/knowledgeDocumentViewer/components/metadata/pageMetadataEditSectionStyles'
import type { MetadataEditScope } from 'features/knowledgeDocumentViewer/types/metadataEditScope'
import type { MetadataDisplayField } from 'features/knowledgeDocumentViewer/utils/pageMetadataFields'
import { Pencil } from 'lucide-react'
import React from 'react'

export type MetadataEditFormChromeProps = {
  title: string
  canEdit: boolean
  isEditing: boolean
  isLoading: boolean
  isSaving: boolean
  isFamilyLoading: boolean
  editAriaLabel: string
  cancelAriaLabel: string
  saveAriaLabel: string
  confirmMessage: string
  saveLabel: string
  savingLabel?: string
  familySaveLabel: string
  leaveFamilyWarning?: string
  crossSurfaceCascadeWarning?: string | undefined
  editScope: MetadataEditScope | undefined
  hasRevisionFamily: boolean
  familySize: number
  currentFilename?: string
  metadataSurface?: MetadataEditSurface
  editableFields: readonly MetadataDisplayField[]
  lockedLabels: ReadonlySet<string>
  onEditClick: () => void
  onCancelEdit: () => void
  onSaveEdit: () => void
  onChangeScope: () => void
  onFieldChange: (label: string, value: string) => void
  children: React.ReactNode
}

function resolveSaveLabel({
  isSaving,
  savingLabel,
  editScope,
  hasRevisionFamily,
  familySaveLabel,
  saveLabel,
}: {
  isSaving: boolean
  savingLabel: string
  editScope: MetadataEditScope | undefined
  hasRevisionFamily: boolean
  familySaveLabel: string
  saveLabel: string
}): string {
  if (isSaving) return savingLabel
  if (editScope === 'family' && hasRevisionFamily) return familySaveLabel
  return saveLabel
}

export function MetadataEditFormChrome({
  title,
  canEdit,
  isEditing,
  isLoading,
  isSaving,
  isFamilyLoading,
  editAriaLabel,
  cancelAriaLabel,
  saveAriaLabel,
  confirmMessage,
  saveLabel,
  savingLabel,
  familySaveLabel,
  leaveFamilyWarning,
  crossSurfaceCascadeWarning,
  editScope,
  hasRevisionFamily,
  familySize,
  currentFilename,
  metadataSurface,
  editableFields,
  lockedLabels,
  onEditClick,
  onCancelEdit,
  onSaveEdit,
  onChangeScope,
  onFieldChange,
  children,
}: MetadataEditFormChromeProps): React.ReactElement {
  const { t } = useLingui()
  const showLeaveFamilyWarning =
    leaveFamilyWarning !== undefined && leaveFamilyWarning.length > 0
  const warningText = showLeaveFamilyWarning
    ? `${leaveFamilyWarning}${
        crossSurfaceCascadeWarning ? ` ${crossSurfaceCascadeWarning}` : ''
      }`
    : crossSurfaceCascadeWarning

  return (
    <Box
      display='flex'
      direction='column'
      gap='md'
      {...(isEditing ? metadataFillLayout : {})}>
      <Box
        display='flex'
        align='center'
        justify='between'
        gap='md'
        shrink={false}>
        <Text as='h3' variant='body' weight='semibold'>
          {title}
        </Text>
        {canEdit && !isEditing ? (
          <Button
            type='button'
            variant='ghost'
            size='default'
            disabled={isLoading || isSaving || isFamilyLoading}
            aria-label={editAriaLabel}
            onClick={onEditClick}>
            <Pencil />
            {t`Edit`}
          </Button>
        ) : null}
      </Box>

      {isEditing && editScope !== undefined && hasRevisionFamily ? (
        <ActiveEditScopeBanner
          editScope={editScope}
          familySize={familySize}
          currentFilename={currentFilename}
          metadataSurface={metadataSurface}
          onChangeScope={onChangeScope}
        />
      ) : null}

      {isEditing ? (
        <>
          <ScrollFade>
            <EditableMetadataFieldList
              fields={editableFields}
              disabled={isSaving}
              lockedLabels={lockedLabels}
              onFieldChange={onFieldChange}
            />
          </ScrollFade>
          <Box display='flex' direction='column' gap='md' shrink={false}>
            {warningText ? (
              <WarningBanner role='status'>
                <WarningIcon size={16} aria-hidden />
                <Text variant='body'>{warningText}</Text>
              </WarningBanner>
            ) : null}
            <ConfirmBanner role='status'>
              <BannerIcon size={16} aria-hidden />
              <Text variant='body'>{confirmMessage}</Text>
            </ConfirmBanner>
            <Box display='grid' gridColumns='two' gap='sm'>
              <Button
                type='button'
                variant='inputRaised'
                size='lg'
                disabled={isSaving}
                aria-label={cancelAriaLabel}
                onClick={onCancelEdit}>
                {t`Cancel`}
              </Button>
              <Button
                type='button'
                variant='primaryRaised'
                size='lg'
                disabled={isSaving}
                aria-label={saveAriaLabel}
                onClick={onSaveEdit}>
                {resolveSaveLabel({
                  isSaving,
                  savingLabel: savingLabel ?? t`Saving…`,
                  editScope,
                  hasRevisionFamily,
                  familySaveLabel,
                  saveLabel,
                })}
              </Button>
            </Box>
          </Box>
        </>
      ) : (
        children
      )}
    </Box>
  )
}
